/**
 * REST API Adapter
 *
 * Provides backwards compatibility with @general-backoffice/core RestApi interface.
 * This adapter wraps the new API client to match the existing backoffice API usage patterns.
 */
import { getApiClient, initApiClient, isApiClientInitialized } from "../client";
import { ApiError } from "../schemas/common.schemas";
/**
 * Create a custom ApiError that matches the backoffice pattern
 */
function createBackofficeApiError(error) {
    const response = error.response?.data;
    // Extract message from Strapi error format
    let message = error.message || "Unknown error";
    if (response?.error && typeof response.error === "object") {
        const strapiError = response.error;
        if (strapiError.message) {
            message = strapiError.message;
        }
    }
    else if (response?.message && typeof response.message === "string") {
        message = response.message;
    }
    const status = error.response?.status || 500;
    return new ApiError(message, status);
}
/**
 * REST API Adapter class
 *
 * Provides the same interface as @general-backoffice/core RestApi
 * but uses the shared API client internally.
 */
class RestApiAdapter {
    constructor(config) {
        this.initialized = false;
        this.config = config;
    }
    /**
     * Ensure the API client is initialized
     */
    ensureInitialized() {
        if (!this.initialized && !isApiClientInitialized()) {
            const clientConfig = {
                baseURL: this.config.path,
                processError: this.config.processError || createBackofficeApiError,
            };
            initApiClient(clientConfig);
            this.initialized = true;
        }
    }
    /**
     * Set the authentication token
     */
    set token(value) {
        this.ensureInitialized();
        getApiClient().setToken(value);
    }
    /**
     * Get the authentication token
     */
    get token() {
        this.ensureInitialized();
        return getApiClient().getToken();
    }
    /**
     * GET request - returns response data directly (not wrapped in AxiosResponse)
     */
    async get(url, config) {
        this.ensureInitialized();
        const response = await getApiClient().get(url, config);
        return response.data;
    }
    /**
     * POST request - returns response data directly
     */
    async post(url, data, config) {
        this.ensureInitialized();
        const response = await getApiClient().post(url, data, config);
        return response.data;
    }
    /**
     * PUT request - returns response data directly
     */
    async put(url, data, config) {
        this.ensureInitialized();
        const response = await getApiClient().put(url, data, config);
        return response.data;
    }
    /**
     * PATCH request - returns response data directly
     */
    async patch(url, data, config) {
        this.ensureInitialized();
        const response = await getApiClient().patch(url, data, config);
        return response.data;
    }
    /**
     * DELETE request - returns response data directly
     */
    async delete(url, config) {
        this.ensureInitialized();
        const response = await getApiClient().delete(url, config);
        return response.data;
    }
    /**
     * Upload file
     */
    async uploadFile(file, field) {
        this.ensureInitialized();
        const response = await getApiClient().uploadFile(file, field);
        return response.data;
    }
}
/**
 * Create a new RestApiAdapter instance
 *
 * @example
 * ```typescript
 * import { createRestApiAdapter } from '@investhinks/api-client';
 *
 * const Api = createRestApiAdapter({ path: 'https://api.investhinks.com/api' });
 *
 * // Set token after login
 * Api.token = jwt;
 *
 * // Make requests
 * const [data, meta] = await Api.get('/bot-configs/me');
 * ```
 */
export function createRestApiAdapter(config) {
    return new RestApiAdapter(config);
}
export { RestApiAdapter };
